import React, { useEffect } from 'react';
import {
  Typography,
  Container,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  Avatar, // We'll style this to be rectangular or use a Box
  ListItemText,
  Divider,
  Chip,
  Paper,
  Stack,
  createTheme,
  ThemeProvider,
  CssBaseline,
  ListItemIcon,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { get } from '../utility/fetch';

// Example group data (replace with actual data and image URLs)
const groups = [
  { id: 1, name: 'Healing Streeess', image: 'https://i.imgur.com/healing-stress.jpg', members: '12,453 Members', newPosts: '100+ new post' },
  { id: 2, name: 'UnJunkles', image: 'https://i.imgur.com/unjunkles.jpg', members: '12,453 Members', newPosts: '100+ new post' },
  { id: 3, name: 'Fitness & Wellness', image: 'https://i.imgur.com/fitness-wellness.jpg', members: '12,453 Members', newPosts: '100+ new post' },
  { id: 4, name: 'Team Organic', image: 'https://i.imgur.com/team-organic.jpg', members: '12,453 Members', newPosts: '100+ new post' },
  { id: 5, name: 'Mother & Child', image: 'https://i.imgur.com/mother-child.jpg', members: '12,453 Members', newPosts: '100+ new post' },
  // Add more groups as needed
];

// Fallback image URLs if not provided:
// const defaultGroupImage = "https://via.placeholder.com/60x60/CCCCCC/FFFFFF?text=Group";

// Custom MUI theme for consistent styling
const theme = createTheme({
  palette: {
    primary: {
      main: '#28a745', // Green for accents
    },
    secondary: {
      main: '#f8f9fa', // Light background for the page
    },
    background: {
      default: '#f8f9fa',
      paper: '#fff', // White background for the group list Paper
    },
    text: {
      primary: '#333',
      secondary: '#6c757d',
    },
  },
  typography: {
    fontFamily: 'Roboto, sans-serif',
    h5: {
      fontWeight: 600,
      fontSize: '1.8rem',
      '@media (max-width:600px)': {
        fontSize: '1.5rem',
      },
    },
    subtitle1: { // For group names
      fontWeight: 500,
      fontSize: '1.05rem',
    },
    body2: { // For members and new posts count
      fontSize: '0.9rem',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '16px', // Rounded corners for the main group list card
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)', // Subtle shadow
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '25px', // Rounded search bar
            backgroundColor: '#f5f5f5', // Light background
            '& fieldset': {
              borderColor: 'transparent', // No border by default
            },
            '&:hover fieldset': {
              borderColor: '#e0e0e0', // Subtle border on hover
            },
            '&.Mui-focused fieldset': {
              borderColor: '#28a745', // Green border on focus
            },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#6c757d', // Default icon button color
        },
      },
    },
    MuiChip: {
        styleOverrides: {
            root: {
                backgroundColor: '#eafcec', // Light green background for chips
                color: "green", // Green text
                borderRadius: '8px', // Rounded corners for chips
                fontWeight: 500,
                padding: '4px 8px', // Adjust padding
                // Style for the delete icon
                '& .MuiChip-deleteIcon': {
                    color: "green", // Green 'x' icon
                    fontSize: '1rem',
                },
            },
        },
    },
    MuiListItemText: {
        styleOverrides: {
            primary: {
                fontWeight: 600, // Make names bolder
                lineHeight: 1.2,
            },
            secondary: {
                color: "grey", // Grey for members and posts
            }
        }
    },
    // Custom style for the rectangular group images within ListItemAvatar
    MuiListItemAvatar: {
        styleOverrides: {
            root: {
                minWidth: 'unset', // Remove default min-width to control spacing better
                marginRight: '16px', // Space between image and text
            },
        },
    },
    MuiAvatar: { // Styling if we decide to use Avatar component but make it square
        styleOverrides: {
            root: {
                borderRadius: '8px', // Make it square with slight rounded corners
                width: 60, // Fixed width
                height: 60, // Fixed height
            }
        }
    }
  },
});

function GroupJoinedList({selectedGroup, setSelectedGroup}) {
    const [filters, setFilters] = React.useState(['Group Size', 'Number of Post', 'Ratings']);
    const [groups, setGroups] = React.useState([]);

    const getGroupJoined = async () => {
        let res = await get("/communitygroup/list");
        if (res.statusCode === 200) {
            // Get current user ID
            const currentUserId = Number(sessionStorage.getItem("userId"));
            
            // Filter groups where user is a member
            const filteredGroups = res.data.filter(group => 
                group.communityGroupMembers?.some(member => 
                    (member.patient?.id === currentUserId) || 
                    (member.employee?.id === currentUserId)
                )
            );
            
            setGroups(filteredGroups);
        }
    };


    const handleDeleteFilter = (chipToDelete) => () => {
        setFilters((chips) => chips.filter((chip) => chip !== chipToDelete));
    };


    useEffect(()=>{
        getGroupJoined();
    }, [])

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ backgroundColor: theme.palette.background.default, width:"100%", minHeight: '100vh', py: 4 }}>
        <Container sx={{width:"80%"}}> {/* Adjusted maxWidth for the list layout */}
          {/* Header */}
          <Typography variant="h5" component="h1" gutterBottom sx={{ mb: 3, ml: { xs: 0, sm: 2 } }}>
            Group Joined
          </Typography>

          {/* Search and Filter */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mb: 3, px: { xs: 0, sm: 2 } }}
          >
            <TextField
              variant="outlined"
              placeholder="Search for Group"
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <IconButton aria-label="filter">
              <FilterListIcon />
            </IconButton>
          </Stack>

          {/* Filter Chips */}
          <Stack
            direction="row"
            spacing={1}
            sx={{
              mb: 3,
              px: { xs: 0, sm: 2 },
              flexWrap: 'wrap', // Allow chips to wrap to the next line
              '& > *': { mb: { xs: 1, sm: 0 } }, // Add bottom margin for wrapped chips
            }}
          >
            {filters.map((data) => (
              <Chip
                key={data}
                label={data}
                onDelete={handleDeleteFilter(data)}
              />
            ))}
          </Stack>

          {/* Group List */}
          <Paper elevation={0} sx={{ borderRadius: '16px', overflow: 'auto' }}>
            <List disablePadding>
              {groups.map((group, index) => (
                <React.Fragment key={group.id}>
                  <ListItem
                    button // Makes the entire item clickable with ripple effect
                    sx={{
                      py: 1.5,
                      px: { xs: 2, sm: 3 }, // Responsive padding
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)', // Subtle hover effect
                        cursor: 'pointer', // Pointer cursor on hover
                        transform: 'scale(1.01)', // Slight scale effect on hover
                      },
                    }}
                    onClick={() => setSelectedGroup(group)} // Handle group selection
                  >
                    <ListItemAvatar>
                      {/* Using Box for the image to maintain rectangular shape */}
                      <Box
                        sx={{
                          width: 60, // Fixed width
                          height: 60, // Fixed height
                          borderRadius: '8px', // Rounded corners for the image
                          backgroundImage: `url("${encodeURI(group?.pictureUrl)}")`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          flexShrink: 0, // Prevent image from shrinking
                        }}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" noWrap>
                          {group.name}
                        </Typography>
                      }
                      secondary={
                        <React.Fragment>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {group.communityGroupMembers?.length + " members"} {/* Assuming this is the members count */}
                          </Typography>
                          <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }} noWrap>
                            {group.newPosts}
                          </Typography>
                        </React.Fragment>
                      }
                      sx={{ ml: 1 }} // Margin left for text next to image
                    />
                    <ListItemIcon sx={{ minWidth: 40, ml: 1 }}> {/* Adjust minWidth for arrow alignment */}
                      <ChevronRightIcon color="action" />
                    </ListItemIcon>
                  </ListItem>
                  {index < groups.length - 1 && <Divider component="li" sx={{ ml: { xs: '80px', sm: '92px' }, mr: 2 }} />} {/* Indented divider, adjust based on image/avatar size */}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default GroupJoinedList;